import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showTournamentSuccess } from '../../../utils/Confirmalert';
import { Api } from '../../../services/interceptor';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEY } from '../../../services/queryEndPoint';
import { selectSelectedLeagueId } from '../../../redux/slice/selectedLeagueSlice';
import { useAppSelector } from '../../../redux/hooks';
import { getBallTypeLabel, getMatchTypeLabel, getTournamentCategoryLabel, type BallType, type MatchType, type TournamentCategory } from './types/createtournament';
import { buildCreateTournamentPayload } from './api/tournamentPayload';
import { clearTournamentDraft } from '../../../redux/slice/createTournamentSlice';
import { setActiveTab } from '../../../redux/slice/activeTabSlice';
import { setTournamentMatches } from '../../../redux/slice/matchesUiSlice';
import { useNavigate } from 'react-router-dom';
import { getAuthToken, getGuestModeFromStorage } from '../../../helper/helper';
import { openAuth } from '../../../redux/slice/authModalSlice';
import './tournamentsDetails/TournamentAboutTab.scss';
import './tournamentDetailsPreview.scss';


const yesNo = (v?: boolean | null) => (v ? 'Yes' : 'No');
const safe = (v: any, fallback = '—') =>
  v === null || v === undefined || v === '' ? fallback : String(v);

type Props = {
  onEdit?: () => void;
  /** When provided, used instead of default for PROCEED NEXT. For Create League wizard. Passes tournamentId from API response. */
  onTournamentCreatedSuccess?: (tournamentId?: string) => void;
  /** When provided, used for BACK TO HOME. For Create League wizard. */
  onTournamentCreatedBackToHome?: () => void;
};

const TournamentDetailsPreview: React.FC<Props> = ({
  onEdit,
  onTournamentCreatedSuccess,
  onTournamentCreatedBackToHome,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const draft = useSelector((s: any) => s?.createTournament?.draft);
  const selectedLeagueId = useAppSelector(selectSelectedLeagueId);

  const { mutate, isPending } = useMutation({
    mutationKey: [QUERY_KEY.addTournament, selectedLeagueId],
    mutationFn: async (payload: { draft: any; leagueId: string | null }) => {
      const body = buildCreateTournamentPayload(payload.draft, payload.leagueId);
      const formData = new FormData();
      // Backend signature: @RequestParam Map<String,String> fields + @RequestParam("bannerUri") MultipartFile
      // → flatten primitive fields into FormData; skip the nested bannerImage object.
      Object.entries(body).forEach(([key, value]) => {
        if (key === 'bannerImage' || value == null) return;
        const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
        formData.append(key, stringValue);
      });

      const bannerBase64 = payload.draft?.bannerBase64 as string | undefined;
      const bannerExt = (payload.draft?.bannerFileType as string | undefined) || 'png';
      // bannerFileType on the draft is the bare extension (e.g. "png"); the Blob
      // mime type still needs the full "image/<ext>" form.
      const bannerMime = bannerExt.includes('/') ? bannerExt : `image/${bannerExt}`;
      const bannerFileName = (payload.draft?.bannerFileName as string | undefined) || `banner.${bannerExt}`;
      if (bannerBase64) {
        const byteString = atob(bannerBase64);
        const buf = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i += 1) buf[i] = byteString.charCodeAt(i);
        formData.append('bannerUri', new Blob([buf], { type: bannerMime }), bannerFileName);
      } else {
        // backend requires the bannerUri part — send empty blob so multipart parsing succeeds
        formData.append('bannerUri', new Blob([], { type: 'application/octet-stream' }), '');
      }

      const res = await Api.post(QUERY_KEY.addTournament, formData);
      return res?.data;
    },
    onSuccess: async (data: unknown) => {
      /** Standalone `/tournaments/create-tournament` does not pass `onTournamentCreatedSuccess`. */
      const defaultStandaloneAfterCreate = () => {
        dispatch(clearTournamentDraft());
        dispatch(setTournamentMatches(false));
        dispatch(setActiveTab('tournaments'));
        navigate('/leagues-home');
      };

      const defaultBackToHome = () => {
        dispatch(clearTournamentDraft());
        dispatch(setTournamentMatches(false));
        dispatch(setActiveTab('leagues-home'));
        sessionStorage.setItem('activeTab', 'leagues-home');
        navigate('/leagues-home');
      };

      const extractId = (obj: unknown): string => {
        if (!obj || typeof obj !== 'object') return '';
        const o = obj as Record<string, unknown>;
        const candidates = [
          o.tournamentId,
          o.id,
          o.tournament_id,
          (o.data as Record<string, unknown>)?.tournamentId,
          (o.data as Record<string, unknown>)?.id,
        ];
        for (const c of candidates) {
          if (c != null && String(c).trim()) return String(c).trim();
        }
        return '';
      };

      let tournamentId = extractId(data);

      showTournamentSuccess({
        onProceed: async () => {
          let resolvedId = tournamentId;
          if (!resolvedId && selectedLeagueId) {
            // Resolve by matching the just-submitted tournamentName from the draft.
            // Picking list[0] is wrong — it's almost always an older tournament.
            try {
              const res = await Api.get(
                `${QUERY_KEY.getTournamentsByLeagues}/${selectedLeagueId}`
              );
              const list = (res?.data as {
                tournamentResponses?: { tournamentId?: string; tournamentName?: string; createdAt?: string; startDate?: string }[];
              })?.tournamentResponses ?? [];
              const draftName = (draft?.tournamentName as string | undefined)?.trim().toLowerCase();
              const matchedByName = draftName
                ? list.find((t) => (t.tournamentName ?? '').trim().toLowerCase() === draftName)
                : undefined;
              // If no name match (e.g. duplicate names), fall back to the most recent entry.
              const sortedDesc = [...list].sort((a, b) => {
                const at = Date.parse(a.createdAt ?? a.startDate ?? '') || 0;
                const bt = Date.parse(b.createdAt ?? b.startDate ?? '') || 0;
                return bt - at;
              });
              resolvedId = matchedByName?.tournamentId ?? sortedDesc[0]?.tournamentId ?? '';
            } catch {
              resolvedId = '';
            }
          }

          await queryClient.invalidateQueries({
            queryKey: selectedLeagueId
              ? [QUERY_KEY.getTournamentsByLeagues, selectedLeagueId]
              : [QUERY_KEY.getTournamentsByLeagues],
          });

          if (onTournamentCreatedSuccess) {
            onTournamentCreatedSuccess(resolvedId || undefined);
          } else {
            defaultStandaloneAfterCreate();
          }
        },
        onBackToHome: onTournamentCreatedBackToHome ?? defaultBackToHome,
      });
    },
    onError: (err) => {
      console.error('Create Tournament failed:', err);
      const status = (err as { response?: { status?: number } })?.response?.status;
      const message =
        status === 401
          ? 'Session expired or you need to log in. Please sign in and try again.'
          : 'Failed to create tournament. Please try again.';
      alert(message);
    },
  });

  const phoneDisplay = useMemo(() => {
    if (!draft) return '—';
    const dial = draft.organizerPhoneNumberCountryCode ? `+${draft.organizerPhoneNumberCountryCode}` : '';
    const num = draft.organizerPhoneNumber || '';
    return [dial, num].filter(Boolean).join(' ').trim() || '—';
  }, [draft]);

  const mt = draft?.matchType as string | undefined;
  const bt = draft?.ballType as string | undefined;
  const cat = draft?.tournamentCategory as string | undefined;
  const showOtherLimited = mt === 'OTHER_LIMITED_OVER';

  const saveTournament = () => {
    if (!draft) return;
    const token = getAuthToken();
    const isGuest = getGuestModeFromStorage();
    if (!token || isGuest) {
      dispatch(openAuth('signin'));
      alert('Please log in to create a tournament.');
      return;
    }
    mutate({ draft, leagueId: selectedLeagueId ?? null });
  };

  const bannerSrc = useMemo(() => {
    if (!draft) return null;
    const b64 = (draft as any).bannerBase64 as string | undefined;
    const ft = (draft as any).bannerFileType as string | undefined;
    if (b64 && ft) {
      const mime = ft.includes('/') ? ft : `image/${ft}`;
      return `data:${mime};base64,${b64}`;
    }
    if ((draft as any).bannerUri) return (draft as any).bannerUri as string;
    return null;
  }, [draft]);

  const dateRange = useMemo(() => {
    const s = draft?.startDate;
    const e = draft?.endDate;
    if (s && e) return `${s} – ${e}`;
    return s || e || '—';
  }, [draft]);

  const scheduledDays = useMemo(() => {
    if (!draft?.startDate || !draft?.endDate) return '—';
    const s = new Date(draft.startDate);
    const e = new Date(draft.endDate);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return '—';
    const diff = Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    return diff > 0 ? String(diff) : '—';
  }, [draft]);

  return (
    <div className="tournament-details-preview">
      <div className="common-stepper-details-wrap preview-wrap">
        <div className="about-tab tournament-about-tab tdp-about">
          {/* Hero */}
          <section className="about-card about-card--hero">
            <div className="about-hero-banner">
              {bannerSrc ? (
                <img
                  className="about-hero-banner__img"
                  src={bannerSrc}
                  alt={draft?.tournamentName ?? 'Tournament banner'}
                />
              ) : (
                <div className="about-hero-banner__placeholder" aria-hidden />
              )}
              <div className="about-hero-scrim" aria-hidden />
              <div className="about-hero-overlay">
                <div className="about-hero-meta-top">
                  {cat && (
                    <span className="about-hero-tag">
                      {getTournamentCategoryLabel(cat as TournamentCategory | null)}
                    </span>
                  )}
                </div>
                <h2 className="about-hero-title">{safe(draft?.tournamentName)}</h2>
                <div className="about-hero-dates">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  <span>{dateRange}</span>
                </div>
              </div>
            </div>
            <div className="about-hero-stats">
              <div className="about-hero-stat">
                <span className="about-hero-stat__label">Match type</span>
                <span className="about-hero-stat__value">{getMatchTypeLabel(mt as MatchType | null)}</span>
              </div>
              <div className="about-hero-stat">
                <span className="about-hero-stat__label">Ball type</span>
                <span className="about-hero-stat__value">{getBallTypeLabel(bt as BallType | null)}</span>
              </div>
              <div className="about-hero-stat">
                <span className="about-hero-stat__label">Max overs</span>
                <span className="about-hero-stat__value">{safe(draft?.maxOvers)}</span>
              </div>
              <div className="about-hero-stat">
                <span className="about-hero-stat__label">Scheduled days</span>
                <span className="about-hero-stat__value">{scheduledDays}</span>
              </div>
            </div>
          </section>

          <div className="about-grid">
            {/* Organizer */}
            <section className="about-card">
              <h3 className="about-card__title">Organizer</h3>
              <div className="about-card__body">
                <div className="about-row">
                  <span className="about-row__label">Name</span>
                  <span className="about-row__value">{safe(draft?.organizerName)}</span>
                </div>
                <div className="about-row">
                  <span className="about-row__label">Email</span>
                  <span className="about-row__value">{safe(draft?.organizerEmailId)}</span>
                </div>
                <div className="about-row">
                  <span className="about-row__label">Phone</span>
                  <span className="about-row__value">{phoneDisplay}</span>
                </div>
              </div>
            </section>

            {/* Match config */}
            <section className="about-card">
              <h3 className="about-card__title">Match Configuration</h3>
              <div className="about-card__body">
                <div className="about-row">
                  <span className="about-row__label">Max overs / bowler</span>
                  <span className="about-row__value">{safe(draft?.maxOversPerBowler)}</span>
                </div>
                {showOtherLimited && (
                  <>
                    <div className="about-row">
                      <span className="about-row__label">No. of overs</span>
                      <span className="about-row__value">{safe(draft?.maxOvers)}</span>
                    </div>
                    <div className="about-row">
                      <span className="about-row__label">No. of wickets</span>
                      <span className="about-row__value">{safe(draft?.maxWickets)}</span>
                    </div>
                  </>
                )}
                <div className="about-row">
                  <span className="about-row__label">Lock roster</span>
                  <span className="about-row__value">{yesNo(draft?.isRosterLocked)}</span>
                </div>
                <div className="about-row">
                  <span className="about-row__label">Allow others to edit score</span>
                  <span className="about-row__value">{yesNo(draft?.canEditScoreCard)}</span>
                </div>
                <div className="about-row">
                  <span className="about-row__label">Allow impact player</span>
                  <span className="about-row__value">{yesNo(draft?.isImpactPlayerAllowed)}</span>
                </div>
              </div>
            </section>

            {/* Points */}
            <section className="about-card">
              <h3 className="about-card__title">Points & Rules</h3>
              <div className="about-card__body">
                <div className="about-row">
                  <span className="about-row__label">Points for win</span>
                  <span className="about-row__value">{safe(draft?.pointForWin)}</span>
                </div>
                <div className="about-row">
                  <span className="about-row__label">Points for tie</span>
                  <span className="about-row__value">{safe(draft?.pointForTie)}</span>
                </div>
                <div className="about-row">
                  <span className="about-row__label">Points for abandoned</span>
                  <span className="about-row__value">{safe(draft?.pointForAbandoned)}</span>
                </div>
              </div>
            </section>
          </div>

          {/* Description */}
          {draft?.description && (
            <section className="about-card about-card--full">
              <h3 className="about-card__title">About</h3>
              <p className="about-description">{draft.description}</p>
            </section>
          )}

          <div className="tdp-actions">
            <button
              type="button"
              className="btn btn-light"
              onClick={() => {
                if (onEdit) onEdit();
              }}
            >
              EDIT
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={saveTournament}
              disabled={isPending}
            >
              {isPending ? 'Saving…' : 'SAVE & CONTINUE'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TournamentDetailsPreview;
